import React, { useState } from 'react';
import {
    FiClipboard,
    FiPhone,
    FiPhoneOff,
    FiDollarSign,
    FiCalendar,
    FiSend,
    FiAlertTriangle,
    FiCheckCircle,
    FiTruck,
    FiPrinter,
    FiXCircle,
    FiCornerUpLeft,
} from 'react-icons/fi';
import { FaHourglassHalf } from 'react-icons/fa';
import styles from './ChangeOrderStatusModal.module.css';
import myAxios from '../AxiosUrl';
import { toast } from 'react-toastify';

function ChangeOrderStatusModal({ setChangeStatusOpen, data, setRefresh, setSelectedItems }) {
    const uid = localStorage.getItem('id');
    const [loading, setLoading] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('');

    /**
     * All 13 available order statuses for bulk updates
     * Order Status Reference (see backend models/order.js):
     * 1  = New Order
     * 2  = Call Done
     * 3  = Courier Done
     * 4  = Print Done
     * 5  = Cancel
     * 6  = Return
     * 7  = Call Not Received
     * 8  = Ask Advance
     * 9  = Ship Later
     * 10 = Waiting
     * 11 = Overseas
     * 12 = Delivery Issue
     * 13 = Payment Received
     */
    const statusOptions = [
        { value: '1', label: 'New', color: '#2E5BFF', icon: FiClipboard },
        { value: '2', label: 'Call Done', color: '#00B8D9', icon: FiPhone },
        { value: '7', label: 'Call Not Received', color: '#FF5630', icon: FiPhoneOff },
        { value: '8', label: 'Ask Advance', color: '#FFC400', icon: FiDollarSign },
        { value: '9', label: 'Ship Later', color: '#00B8D9', icon: FiCalendar },
        { value: '10', label: 'Waiting', color: '#8777D9', icon: FaHourglassHalf },
        { value: '11', label: 'Overseas', color: '#FFAB00', icon: FiSend },
        { value: '12', label: 'Delivery Issue', color: '#FF5630', icon: FiAlertTriangle },
        { value: '13', label: 'Payment Received', color: '#36B37E', icon: FiCheckCircle },
        { value: '3', label: 'Courier Done', color: '#36B37E', icon: FiTruck },
        { value: '4', label: 'Print Done', color: '#6554C0', icon: FiPrinter },
        { value: '5', label: 'Cancel', color: '#97A0AF', icon: FiXCircle },
        { value: '6', label: 'Return', color: '#FF8B00', icon: FiCornerUpLeft },
    ];

    const handleUpdateStatus = () => {
        if (!selectedStatus) {
            toast.warning('Please select a status');
            return;
        }

        setLoading(true);
        setRefresh(false);

        const orderIds = data.map((order) => order._id);
        const url = `edit-all-order/${uid}`;

        myAxios
            .post(url, { Status: selectedStatus, Ids: orderIds })
            .then((res) => {
                const statusLabel = statusOptions.find(s => s.value === selectedStatus)?.label;
                toast.success(`${data.length} order(s) updated to ${statusLabel}!`);
                setLoading(false);
                setChangeStatusOpen(false);
                setRefresh(true);
                setSelectedItems([]);
            })
            .catch((error) => {
                console.error('Error updating status:', error);
                toast.error('Failed to update order status');
                setLoading(false);
            });
    };

    return (
        <div className={styles.modalOverlay} onClick={() => setChangeStatusOpen(false)}>
            <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.modalHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div className={styles.headerIcon}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                <polyline points="9 11 12 14 22 4" />
                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                            </svg>
                        </div>
                        <div>
                            <h2 className={styles.headerTitle}>Change Order Status</h2>
                            <p className={styles.headerSubtitle}>
                                {data?.length === 1
                                    ? '1 order selected'
                                    : `${data?.length || 0} orders selected`}
                            </p>
                        </div>
                    </div>
                    <button className={styles.closeBtn} onClick={() => setChangeStatusOpen(false)}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className={styles.modalContent}>
                    <p className={styles.description}>
                        Select a new status for the selected orders. All selected orders will be updated to this status.
                    </p>

                    {/* Status Options as Cards */}
                    <div className={styles.statusGrid}>
                        {statusOptions.map((status) => (
                            <div
                                key={status.value}
                                className={`${styles.statusCard} ${
                                    selectedStatus === status.value ? styles.statusCardActive : ''
                                }`}
                                onClick={() => setSelectedStatus(status.value)}
                            >
                                <div className={styles.statusIcon}>{status.icon && <status.icon />}</div>
                                <div className={styles.statusLabel}>{status.label}</div>
                            </div>
                        ))}
                    </div>

                    {/* Show selected orders info */}
                    {data && data.length > 0 && (
                        <div className={styles.ordersInfo}>
                            <div className={styles.infoLabel}>Orders to be updated:</div>
                            <div className={styles.ordersList}>
                                {data.slice(0, 5).map((order) => (
                                    <div key={order._id} className={styles.orderItem}>
                                        <span className={styles.orderInvoice}>#{order.InvoiceId}</span>
                                        <span className={styles.orderCustomer}>{order.cName}</span>
                                        <span className={styles.orderCurrentStatus}>
                                            {/* Convert to Number for comparison - handle both string and number types */}
                                            {Number(order.orderStatus) === 1 && 'New'}
                                            {Number(order.orderStatus) === 2 && 'Call Done'}
                                            {Number(order.orderStatus) === 3 && 'Courier Done'}
                                            {Number(order.orderStatus) === 4 && 'Print Done'}
                                            {Number(order.orderStatus) === 5 && 'Cancel'}
                                            {Number(order.orderStatus) === 6 && 'Return'}
                                            {Number(order.orderStatus) === 7 && 'Call Not Received'}
                                            {Number(order.orderStatus) === 8 && 'Ask Advance'}
                                            {Number(order.orderStatus) === 9 && 'Ship Later'}
                                            {Number(order.orderStatus) === 10 && 'Waiting'}
                                            {Number(order.orderStatus) === 11 && 'Overseas'}
                                            {Number(order.orderStatus) === 12 && 'Delivery Issue'}
                                            {Number(order.orderStatus) === 13 && 'Payment Received'}
                                        </span>
                                    </div>
                                ))}
                                {data.length > 5 && (
                                    <div className={styles.moreOrders}>
                                        +{data.length - 5} more orders
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className={styles.modalActions}>
                    <button
                        className={styles.cancelButton}
                        onClick={() => setChangeStatusOpen(false)}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        className={styles.updateButton}
                        onClick={handleUpdateStatus}
                        disabled={loading || !selectedStatus}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                        {loading ? 'Updating...' : 'Update Status'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ChangeOrderStatusModal;
